/*
 * Data table, ported from the Tixora template's HeroUI table (primary variant): the grey
 * frame, the quiet header with column dividers, the rows as a rounded card inside. Same
 * props as the kit's Table so a screen switches by changing the import. Styles in
 * styles/data-display.css; rows are DataTableRow (or plain <tr> with <td> cells).
 *
 * `loading` (owner 2026-10-13, motion system): while a read is in flight and there are no rows
 * yet, the frame shows `skeletonRows` shimmering rows of `columns` cells (styles/motion.css
 * `.motion-skeleton`) instead of an empty table, and the empty line waits for the answer.
 */
type DataTableProps = {
    className?: string;
    /** Accessible name of the table. */
    label: string;
    /** The header cells (<th>), the kit Table's shape. */
    cellsThead: React.ReactNode;
    children: React.ReactNode;
    /** Shown inside the frame when there are no rows. */
    empty?: React.ReactNode;
    isEmpty?: boolean;
    /** A read is in flight: skeleton rows stand in until the first rows arrive. */
    loading?: boolean;
    /** How many cells a skeleton row has; defaults to the header's count when it is an array, else 4. */
    columns?: number;
    skeletonRows?: number;
};

const Skeleton = ({ rows, columns }: { rows: number; columns: number }) => (
    <>
        {Array.from({ length: rows }, (_, row) => (
            <tr className="data-table__skeleton" aria-hidden="true" key={row}>
                {Array.from({ length: columns }, (_, cell) => (
                    <td key={cell}>
                        <span className="motion-skeleton" style={{ width: `${55 + ((row * 7 + cell * 13) % 40)}%` }} />
                    </td>
                ))}
            </tr>
        ))}
    </>
);

const countCells = (cells: React.ReactNode): number => {
    if (Array.isArray(cells)) return cells.length;
    const props = (cells as { props?: { children?: unknown } } | null)?.props;
    return props && Array.isArray(props.children) ? props.children.filter(Boolean).length : 4;
};

const DataTable = ({ className, label, cellsThead, children, empty, isEmpty, loading, columns, skeletonRows = 4 }: DataTableProps) => {
    const hasRows = Array.isArray(children) ? children.some((child) => Array.isArray(child) ? child.length > 0 : Boolean(child)) : Boolean(children);
    const showSkeleton = Boolean(loading) && !hasRows;
    return (
        <div className={`data-table ${className || ""}`} aria-busy={loading || undefined}>
            <div className="data-table__scroll">
                <table className="data-table__content" aria-label={label}>
                    <thead>
                        <tr>{cellsThead}</tr>
                    </thead>
                    <tbody className={showSkeleton ? undefined : "motion-stagger"}>{showSkeleton ? <Skeleton rows={skeletonRows} columns={columns ?? countCells(cellsThead)} /> : children}</tbody>
                </table>
            </div>
            {!showSkeleton && isEmpty && empty ? <div className="data-table__empty motion-reveal">{empty}</div> : null}
        </div>
    );
};

export default DataTable;
